import {
  Button,
  Page,
  PageBody,
  PageHeader,
  PageHeaderTitle,
  PageHeaderDescription,
  PageHeaderActions,
  PageHeaderTabs,
} from '@customerio/pluma-components/react';
import { Helmet } from 'react-helmet-async';
import { Navigate } from 'react-router-dom';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { useAuth } from '@/services/auth';
import { PlanFeature } from '@/services/auth/types';
import { hasUnlimitedPlanFeatureAccess } from '@/services/auth/plan-features';
import { useCustomerFeedback } from '@/features/customer-feedback';
import { useAnalytics } from '@/services/analytics';
import { usePageViewTracking } from '@/hooks/use-page-view-tracking';
import { AnalysisTabs } from '../components/analysis-tabs';
import { DeliverabilityTable } from './components/deliverability-table';
import { CustomSMTPEmptyState } from './components/custom-smtp-empty-state';
import { useDeliverabilityData, useHasMailServers } from './hooks';

export function DeliverabilityPage() {
  const workspaceId = getWorkspaceIdFromCurrentUrl();
  const auth = useAuth();
  const showFeedbackModal = useCustomerFeedback();
  const { track } = useAnalytics();

  const hasUnlimitedAccess = hasUnlimitedPlanFeatureAccess(auth.account, PlanFeature.DeliverabilityAnalytics);

  const {
    rows,
    dateRange,
    searchValue,
    sortingValue,
    isLoading,
    isError,
    hasNoData,
    handleDateRangeChange,
    handleSearch,
    handleSortingChange,
  } = useDeliverabilityData();

  const { hasMailServers, isLoading: isLoadingMailServers } = useHasMailServers();

  usePageViewTracking(() => {
    track('deliverability', 'viewed', {
      workspaceId,
      hasUnlimitedAccess,
    });
  });

  if (!hasUnlimitedAccess && !auth.account.is_masq) {
    return <Navigate to={`/workspaces/${workspaceId}/journeys/analysis`} replace />;
  }

  return (
    <>
      <Helmet>
        <title>Deliverability | Customer.io</title>
      </Helmet>
      <Page style={{ width: '100%' }}>
        <PageHeader>
          <PageHeaderTitle>Analysis</PageHeaderTitle>
          <PageHeaderDescription>
            View, compare, and analyze metrics across your entire workspace.
          </PageHeaderDescription>
          <PageHeaderActions>
            <Button
              variant="secondary"
              onClick={() => {
                void showFeedbackModal({ feature: 'Deliverability Analytics' });
              }}
            >
              Give Feedback
            </Button>
          </PageHeaderActions>
          <PageHeaderTabs>
            <AnalysisTabs />
          </PageHeaderTabs>
        </PageHeader>
        <PageBody>
          {hasMailServers ? (
            <CustomSMTPEmptyState />
          ) : (
            <DeliverabilityTable
              data={rows}
              dateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
              isLoading={isLoading || isLoadingMailServers}
              isError={isError}
              searchValue={searchValue}
              onSearch={handleSearch}
              sortingValue={sortingValue}
              onSortingChange={handleSortingChange}
              hasNoData={hasNoData}
            />
          )}
        </PageBody>
      </Page>
    </>
  );
}
